import { RegionTable } from '@deighton/shared-ui/core';

import { AUTH_REGION_STORAGE_KEY } from './auth-storage';
import { REGION_PICKER_COPY, RegionPickerCopy } from './region-picker-copy';
import { clearedRegionCookie, SELECT_REGION_PATH } from './region-resolver';

const DOCUMENT_TITLE_SUFFIX: string = ' – dTIMS';

/** Root-relative so the wordmark loads on `/select-region` and on deep links. */
const REGION_PICKER_LOGO_SRC: string = '/assets/brand/d-icon.svg';

/** Display width of the wordmark, in CSS pixels. */
const REGION_PICKER_LOGO_WIDTH: number = 200;

/** Display height matching the SVG viewBox (281×108). */
const REGION_PICKER_LOGO_HEIGHT: number = 77;

/**
 * Shows a plain-DOM region picker (Angular has not booted yet) and resolves with the chosen key.
 * Styles live in styles.scss (`.d-region-picker`), so the page needs no inline styles.
 * Copy is loaded from `en.json` at build time (Transloco is not available until after bootstrap).
 * Text is set with textContent only (no innerHTML).
 * @param regions - The region allowlist.
 * @param suggested - A key to preselect (time-zone hint), or null.
 * @param doc - The document to render into.
 * @returns The key of the region the user chose.
 */
export function showRegionPicker(
  regions: RegionTable,
  suggested: string | null,
  doc: Document = document
): Promise<string> {
  return new Promise<string>((resolve: (key: string) => void): void => {
    const copy: RegionPickerCopy = REGION_PICKER_COPY;
    const previousTitle: string = doc.title;
    doc.title = `${copy.documentTitle}${DOCUMENT_TITLE_SUFFIX}`;

    const root: HTMLElement = doc.createElement('main');
    root.className = 'd-region-picker';
    root.setAttribute('aria-labelledby', 'd-region-picker-title');

    appendLogo(root, doc);

    const title: HTMLHeadingElement = doc.createElement('h1');
    title.id = 'd-region-picker-title';
    title.textContent = copy.title;
    root.appendChild(title);

    const hint: HTMLParagraphElement = doc.createElement('p');
    hint.textContent = copy.hint;
    root.appendChild(hint);

    const fieldset: HTMLFieldSetElement = doc.createElement('fieldset');
    fieldset.className = 'd-region-picker__fieldset';

    const legend: HTMLLegendElement = doc.createElement('legend');
    legend.className = 'd-region-picker__options-legend';
    legend.textContent = copy.optionsLegend;
    fieldset.appendChild(legend);

    const list: HTMLDivElement = doc.createElement('div');
    list.className = 'd-region-picker__options';
    fieldset.appendChild(list);

    let preselected: HTMLButtonElement | null = null;
    for (const [key, region] of Object.entries(regions)) {
      const button: HTMLButtonElement = doc.createElement('button');
      button.type = 'button';
      button.className = 'd-region-picker__option';
      button.dataset['region'] = key;

      const label: HTMLSpanElement = doc.createElement('span');
      label.className = 'd-region-picker__option-label';
      label.textContent = region.label;
      button.appendChild(label);

      if (key === suggested) {
        button.classList.add('d-region-picker__option--suggested');
        const suggestedNote: HTMLSpanElement = doc.createElement('span');
        suggestedNote.className = 'd-region-picker__option-suggested';
        suggestedNote.textContent = copy.suggestedLabel;
        button.appendChild(suggestedNote);
        preselected = button;
      }

      button.addEventListener('click', (): void => {
        doc.title = previousTitle;
        root.remove();
        resolve(key);
      });
      list.appendChild(button);
    }

    root.appendChild(fieldset);
    doc.body.appendChild(root);
    (preselected ?? list.querySelector('button'))?.focus();
  });
}

/**
 * Shows a configuration error in place of the app, so a bad deploy fails loudly.
 * @param error - The error to show.
 * @param doc - The document to render into.
 */
export function showBootError(error: unknown, doc: Document = document): void {
  const copy: RegionPickerCopy = REGION_PICKER_COPY;
  doc.title = `${copy.bootErrorTitle}${DOCUMENT_TITLE_SUFFIX}`;

  const root: HTMLElement = doc.createElement('main');
  root.className = 'd-region-picker d-region-picker--error';
  root.setAttribute('role', 'alert');
  appendLogo(root, doc);
  const title: HTMLHeadingElement = doc.createElement('h1');
  title.textContent = copy.bootErrorTitle;
  const detail: HTMLPreElement = doc.createElement('pre');
  detail.textContent = error instanceof Error ? error.message : String(error);

  const backButton: HTMLButtonElement = doc.createElement('button');
  backButton.type = 'button';
  backButton.className = 'd-region-picker__primary-button';
  backButton.textContent = copy.backToRegionSelection;
  backButton.addEventListener('click', (): void => {
    returnToRegionSelection(doc);
  });

  root.append(title, detail, backButton);
  doc.body.appendChild(root);
}

/**
 * Clears the region cookie and the auth-region marker in localStorage so the user can choose again.
 * @param doc - The document whose cookie and storage to update.
 */
export function clearStoredRegionChoice(doc: Document = document): void {
  doc.cookie = clearedRegionCookie();
  try {
    doc.defaultView?.localStorage.removeItem(AUTH_REGION_STORAGE_KEY);
  } catch {
    // Storage may be blocked; the picker path still clears the cookie on load.
  }
}

/**
 * Forgets the saved region and opens {@link SELECT_REGION_PATH}.
 * @param doc - The document to navigate.
 */
export function returnToRegionSelection(doc: Document = document): void {
  clearStoredRegionChoice(doc);
  doc.defaultView?.location.assign(SELECT_REGION_PATH);
}

/**
 * Inserts the dTIMS wordmark at the top of a pre-bootstrap page.
 * @param root - The picker or boot-error container.
 * @param doc - The document to create the image in.
 */
function appendLogo(root: HTMLElement, doc: Document): void {
  const logo: HTMLImageElement = doc.createElement('img');
  logo.className = 'd-region-picker__logo';
  logo.src = REGION_PICKER_LOGO_SRC;
  logo.alt = REGION_PICKER_COPY.logoAlt;
  logo.width = REGION_PICKER_LOGO_WIDTH;
  logo.height = REGION_PICKER_LOGO_HEIGHT;
  root.appendChild(logo);
}
